import { useRef, useState } from 'react';

import UserService from '@/services/user.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import FormError from '@/shared-components/error/components/FormError';

import IssueEvidence from './components/IssueEvidence';

import './index.css';

export default function ReportIssueForm({ orderData, onReportIssue, didReceiveArtwork }) {
  const [formState, setFormState] = useState({
    issue_message: "",
    issue_evidence: null
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  
  // Create a ref for the textarea
  const textareaRef = useRef(null);

  // Handler for textarea change that auto-resizes the textarea.
  const handleTextareaChange = (e) => {
    setFormState({ ...formState, issue_message: e.target.value });
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  async function submitReportIssue(e) {
    e.preventDefault();
    console.log("submitReportIssue called");
    console.log("didReceiveArtwork:", didReceiveArtwork);
    console.log("formState:", formState);
    // Removed stray JSX snippet: <div>Artwork Received: {String(didReceiveArtwork)}</div>,
  
    setFormLoading(true);
    setFormError(null);
  
    // Check if artwork was received and evidence is required but missing
    if (didReceiveArtwork === true && !formState.issue_evidence) {
      console.log("Validation failed: issue_evidence is missing for a received artwork");
      setFormError({ element: "issue_evidence", error_code: "ISSUE_EVIDENCE_PHOTO" });
      setFormLoading(false);
      return;
    }
  
    if (Boolean(formState.issue_evidence) && formState.issue_evidence.loading) {
      console.log("Image is still loading");
      alert("Please wait until the image is loaded or delete it.");
      setFormLoading(false);
      return; // Ensure we exit the function here.
    }
  
    // Build the payload conditionally:
    const payload = {
      issue_message: formState.issue_message,
      issue_artwork_received: didReceiveArtwork,
    };

    // Only add issue_evidence if artwork was received and evidence exists
    if (didReceiveArtwork === true && formState.issue_evidence) {
      payload.issue_evidence = formState.issue_evidence;
    } else {
      // Ensure the key isn't sent at all when not needed
      delete payload.issue_evidence;
    }
  
    // Submit the issue report
    const { response, data } = await UserService.reportIssue(orderData._id, payload);
    console.log("API response:", response, data);
  
    if (response.ok) {
      console.log("Submission successful");
      setFormLoading(false);
      onReportIssue(orderData._id, data);
    } else if (response.status === 400) {
      console.log("Submission error (400):", data);
      setFormLoading(false);
      setFormError({ element: data.error_data.element, error_code: data.error_type });
    }
  }  

  return (
    <>
      <div className={`report-issue-form__container ${didReceiveArtwork === true ? "received" : didReceiveArtwork === false ? "no-receive" : ""}`}>
        <span className="report-issue-form__main-text">
          {
            didReceiveArtwork ?
              "Let the gallery know what happened"
              :
              "If you have not received the artwork"
          }
        </span>

        <span className="report-issue-form__text">
          {
            didReceiveArtwork ?
              "Please share the details of the problem below, and the gallery will try to find the best solution. If necessary, Suarte can be involved at a later stage."
              :
              "Please review your order's shipping status using the tracking number. If done, describe the issue below:"
          }
        </span>

        <form onSubmit={submitReportIssue} className="report-issue-form__form">
          <div className="report-issue-form__textarea-container">
            <textarea 
              id="issue_message"
              ref={textareaRef}  // attach the ref
              className="report-issue-form__textarea"
              placeholder="Write something..."
              disabled={formLoading}
              spellCheck={false}
              value={formState.issue_message}
              onChange={handleTextareaChange}
            />
            <FormError error={formError} element="issue_message"/>
          </div>

          {didReceiveArtwork === true && (
            <>
              <span className="report-issue-form__main-text">
                Add aditional evidence
              </span>

              <span className="report-issue-form__text">
                You can submit an image that clearly demonstrates the issues previously mentioned.
              </span>

              <IssueEvidence 
                formState={formState} 
                setFormState={setFormState}
                setFormError={setFormError}
              />

              <FormError error={formError} element="issue_evidence"/>
            </>
          )}

          <ContinueButton
            loading={formLoading}
            turnOff={!formState.issue_message}
            className="report-issue-form__submit-button"
          >
            Submit
          </ContinueButton>
        </form>
      </div>
    </>
  );
}
